/**
 * SwarmActivity — live view of the agent swarm working (v0.5.9 AE-111).
 *
 * Subscribes to /api/v1/stream/tickets (same SSE feed as TicketsRail) and
 * renders each A2A dispatch as a chip: who asked whom, which skill,
 * in-flight -> answered (with server-measured latency). Requests pair to
 * responses via the response ticket's `parents`. Thesis/order milestones
 * (THESIS_CREATED, ORDER_PENDING_APPROVAL) render as event rows.
 *
 * Honest by construction: renders only tickets the ledger actually
 * emitted; an idle floor shows "floor is quiet", never fake activity.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useStream } from '../lib/useStream';

interface TicketEvent {
  id?: string;
  specialist?: string;
  action?: string;
  mode?: string;
  created_at?: string;
  parents?: string[];
  payload?: Record<string, unknown>;
}

export interface Dispatch {
  requestId: string;
  fromPane: string;
  toPane: string;
  skill: string;
  status: 'in_flight' | 'completed' | 'error';
  latencyMs: number | null;
  mode: string;
}

interface Milestone {
  id: string;
  action: string;
  specialist: string;
  mode: string;
}

const DISPATCH_ACTIONS = new Set(['A2A_REQUEST', 'A2A_RESPONSE']);
const MILESTONE_ACTIONS = new Set(['THESIS_CREATED', 'ORDER_PENDING_APPROVAL']);
const MAX_ROWS = 10;

/** Fold one ticket into the dispatch list (exported for tests). */
export function foldTicket(prev: Dispatch[], t: TicketEvent): Dispatch[] {
  if (t.action === 'A2A_REQUEST' && t.id) {
    const p = t.payload ?? {};
    const d: Dispatch = {
      requestId: t.id,
      fromPane: t.specialist ?? '?',
      toPane: String(p.to_pane ?? '?'),
      skill: String(p.skill ?? '?'),
      status: 'in_flight',
      latencyMs: null,
      mode: t.mode ?? 'sim',
    };
    return [d, ...prev.filter((x) => x.requestId !== t.id)].slice(0, MAX_ROWS);
  }
  if (t.action === 'A2A_RESPONSE') {
    const parent = t.parents?.[0];
    if (!parent) return prev;
    const p = t.payload ?? {};
    const status = p.status === 'error' ? 'error' : 'completed';
    const latency = typeof p.latency_ms === 'number' ? p.latency_ms : null;
    return prev.map((d) =>
      d.requestId === parent ? { ...d, status, latencyMs: latency } : d,
    );
  }
  return prev;
}

export function SwarmActivity({
  title = 'swarm activity',
  collapsible = false,
}: {
  title?: string;
  collapsible?: boolean;
}) {
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [paneNames, setPaneNames] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(!collapsible);

  useEffect(() => {
    let alive = true;
    // Promise.resolve() wrapper: labels are an enhancement — any failure
    // (including partially-mocked api modules in tests) falls back to raw
    // pane ids, which are still honest.
    Promise.resolve()
      .then(() => api.panes.list())
      .then((list) => {
        if (!alive) return;
        setPaneNames(Object.fromEntries(list.map((p) => [p.id, p.name])));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useStream({
    url: '/api/v1/stream/tickets',
    onEvent: ({ event, data }) => {
      if (event !== 'ticket') return;
      const t = data as TicketEvent;
      if (!t.action) return;
      if (DISPATCH_ACTIONS.has(t.action)) {
        setDispatches((prev) => foldTicket(prev, t));
      } else if (MILESTONE_ACTIONS.has(t.action) && t.id) {
        setMilestones((prev) =>
          [
            {
              id: t.id!,
              action: t.action!,
              specialist: t.specialist ?? '?',
              mode: t.mode ?? 'sim',
            },
            ...prev.filter((m) => m.id !== t.id),
          ].slice(0, 4),
        );
      }
    },
  });

  const label = (paneId: string) => paneNames[paneId] ?? paneId;
  const quiet = dispatches.length === 0 && milestones.length === 0;

  return (
    <div data-testid="swarm-activity" className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] text-ink-mute uppercase tracking-wide">
          {title}
        </div>
        {collapsible && (
          <button
            className="btn text-[10px]"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            {open ? 'hide' : `show${dispatches.length ? ` (${dispatches.length})` : ''}`}
          </button>
        )}
      </div>
      {open && (
        <>
          {quiet && (
            <div data-testid="swarm-quiet" className="text-xs text-ink-dim">
              floor is quiet — dispatches appear here as specialists are asked.
            </div>
          )}
          <ul className="space-y-1 max-h-56 overflow-y-auto">
            {dispatches.map((d) => (
              <li
                key={d.requestId}
                data-testid={`swarm-dispatch-${d.requestId}`}
                data-status={d.status}
                className="text-[11px] flex items-center gap-2"
              >
                <span
                  className={
                    d.status === 'in_flight'
                      ? 'inline-block w-1.5 h-1.5 rounded-full bg-warn animate-pulse shrink-0'
                      : d.status === 'error'
                        ? 'inline-block w-1.5 h-1.5 rounded-full bg-bad shrink-0'
                        : 'inline-block w-1.5 h-1.5 rounded-full bg-ok shrink-0'
                  }
                />
                <span className="font-mono text-ink-mute truncate flex-1">
                  {label(d.fromPane)} → {label(d.toPane)} · {d.skill}
                </span>
                <span className="text-ink-dim num whitespace-nowrap">
                  {d.status === 'in_flight'
                    ? 'in flight…'
                    : d.status === 'error'
                      ? 'error'
                      : d.latencyMs != null
                        ? `${(d.latencyMs / 1000).toFixed(1)}s`
                        : 'answered'}
                </span>
              </li>
            ))}
            {milestones.map((m) => (
              <li
                key={m.id}
                data-testid={`swarm-milestone-${m.id}`}
                className="text-[11px] flex items-center gap-2"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                <span className="font-mono text-accent truncate flex-1">
                  {m.action.toLowerCase().replace(/_/g, ' ')} · {m.specialist}
                </span>
                <span
                  className={
                    m.mode === 'live' ? 'text-bad uppercase' : 'text-ok uppercase'
                  }
                >
                  {m.mode}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
